// Показ итога спина: анимации символов, дорожки выигрышных линий и сумма в центре поля.
//
// Показ идёт в две части. Первая — общая: по каждой выигрышной линии слева направо пробегает
// железнодорожный путь, символы трогаются вслед за ним, барабан за барабаном, и каждый играет
// свою анимацию один раз. Когда отыграл последний, в центре поля всплывает сумма выигрыша
// и так же плавно гаснет. Разом с ней игра пускает отклик — дым из-под паровоза.
//
// Крупный выигрыш сумму в поле не показывает: для него мал и счётчик, и само поле. Его называют
// створки, см. Big Win в GDD, раздел 3.7, и FieldCover. Показ тогда кончается вместе с анимациями
// символов, а створки сходятся сразу за ними.
//
// Wild играет свою анимацию всегда, выиграл он или нет: игрок обязан заметить символ, который
// достраивает любую комбинацию. Выигравший wild играет выигрыш вместе со своей линией,
// остальные мигают. Спин, где wild выпал впустую, тем и кончается: показывать сумму не за что.
//
// Вторая часть — цикл: линии показываются по одной, по кругу, до следующего спина. Каждая
// называет свою выплату суммой в середине поля. Раунд спина ждёт только первую часть, поэтому
// цикл крутится уже в ожидании, поверх стоящих барабанов. Так игрок видит, за что ему заплатили,
// и при этом волен крутить дальше в любой миг.
//
// Новый спин снимает показ на любом месте, см. game/spinRound.ts.
//
// Координаты здесь те же, что у барабанов: это пиксели картинки барабанов.

import { Container, Graphics } from 'pixi.js';

import type { GameConfig, GameSymbol, Payline, SymbolId } from '../api/gameConfig';
import { spinPayout, type CellMark, type Field, type FieldRow, type LineWin, type SpinResult } from '../api/spin';
import { REELS_GRID, SYMBOL_BLINK_ANIMATION, SYMBOL_WIN_ANIMATION, WINLINE_SKELETON, markedBlinkAnimation, markedWinAnimation } from '../assets/catalog';
import type { AssetManager, Spine } from '../assets/manager';
import type { SymbolLibrary } from '../game/symbols';

import { AmountLabel, countedAmount } from './amount';
import { columnX, rowY, type Cell } from './reels';

/** На сколько барабанов нарисованы дорожки линий. Другой ширины в ассетах нет. */
const TRACK_REELS: number = 3;

/**
 * Насколько символ следующего барабана трогается позже соседа слева.
 *
 * Шаг взят по ходу дорожки: её маска открывает путь слева направо и проходит середины окон
 * примерно на 0.08, 0.23 и 0.37 секунды, то есть окно за окном через те же 0.15.
 * Символ вспыхивает сразу за пробежавшим по нему путём.
 */
const SYMBOL_STEP_SECONDS: number = 0.15;

/** Сколько сумма выигрыша всплывает и сколько гаснет. */
const FADE_SECONDS: number = 0.3;

/** За сколько счётчик добирается от нуля до всей суммы. */
const COUNT_SECONDS: number = 0.7;

/** Сколько сумма стоит на виду после счёта. */
const HOLD_SECONDS: number = 0.7;

/** Высота цифры суммы в координатах картинки барабанов: чуть больше половины ряда. */
const AMOUNT_HEIGHT: number = 96;

/**
 * Сколько на линию отводится в цикле.
 *
 * Анимация символа идёт секунду, последний символ линии трогается на треть секунды позже
 * первого, а остаток — пауза перед следующей линией.
 */
const LINE_SECONDS: number = 1.6;

/** Имя ячейки поля. По нему показ отличает одну ячейку от другой. */
function cellKey(cell: Cell): string {
  return `${cell.reel}:${cell.row}`;
}

/**
 * Ячейки всех выигрышных линий без повторов.
 *
 * Одна ячейка лежит сразу на нескольких линиях, а анимация ей нужна одна: вторая такая же
 * шла бы поверх первой и мешала бы ей.
 */
export function winningCells(wins: readonly LineWin[]): Cell[] {
  const cells: Cell[] = [];
  const taken = new Set<string>();

  for (const win of wins) {
    win.positions.forEach((row: number, reel: number) => {
      const cell: Cell = { reel, row };
      const key: string = cellKey(cell);

      if (taken.has(key))
        return;

      taken.add(key);
      cells.push(cell);
    });
  }

  return cells;
}

/** Ячейка с анимацией: что играть на этом месте поля. */
export interface AnimatedCell {
  readonly cell: Cell;
  readonly animation: string;
}

/** Число, повешенное надстройкой на ячейку. Null — метки на ней нет. */
function markOf(marks: readonly CellMark[], cell: Cell): number | null {
  const mark: CellMark | undefined = marks.find((each: CellMark) => each.reel === cell.reel && each.row === cell.row);

  return mark === undefined ? null : mark.value;
}

/** Ставит выигравшим ячейкам их анимацию выигрыша. */
export function winAnimations(cells: readonly Cell[], marks: readonly CellMark[] = []): AnimatedCell[] {
  return cells.map((cell: Cell) => {
    const value: number | null = markOf(marks, cell);

    return { cell, animation: value === null ? SYMBOL_WIN_ANIMATION : markedWinAnimation(value) };
  });
}

/**
 * Что играет на поле после остановки барабанов.
 *
 * Выигравшие ячейки играют выигрыш, оставшиеся wild мигают. Wild на выигрышной линии второй
 * анимации не получает: выигрыш он уже играет, и мигание шло бы поверх него.
 *
 * Помеченная ячейка играет анимацию своего значения: скелет рисует цифру сам, см. каталог.
 *
 * @param wild Идентификаторы символов с ролью wild. Роли берутся из описания игры.
 * @param marks Числа, которые надстройка повесила на ячейки поля.
 */
export function animatedCells(field: Field, wins: readonly LineWin[], wild: ReadonlySet<SymbolId>, marks: readonly CellMark[] = []): AnimatedCell[] {
  const cells: AnimatedCell[] = winAnimations(winningCells(wins), marks);
  const taken = new Set<string>(cells.map((animated: AnimatedCell) => cellKey(animated.cell)));

  field.forEach((cellRow: FieldRow, row: number) => {
    cellRow.forEach((id: SymbolId, reel: number) => {
      const cell: Cell = { reel, row };

      if (!wild.has(id) || taken.has(cellKey(cell)))
        return;

      const value: number | null = markOf(marks, cell);

      cells.push({ cell, animation: value === null ? SYMBOL_BLINK_ANIMATION : markedBlinkAnimation(value) });
    });
  });

  return cells;
}

/** Форма дорожки: какую анимацию играть, от какого ряда и надо ли отразить её по вертикали. */
export interface TrackShape {
  readonly animation: string;
  readonly row: number;
  readonly flipped: boolean;
}

/**
 * Подбирает форму дорожки под линию.
 *
 * Нарисованы три формы, и все они начинаются у левого края поля на середине своего ряда:
 * прямая линия, диагональ через всё поле вниз и галка на ряд вверх. Линия той же формы,
 * но в другую сторону, играет той же дорожкой, отражённой по вертикали.
 *
 * Формы, которой в ассетах нет, дорожка не получит: рисовать её нечем.
 */
export function trackFor(payline: Payline): TrackShape | null {
  if (payline.length !== TRACK_REELS)
    return null;

  const [left, middle, right] = payline;

  if (left === middle && middle === right)
    return { animation: WINLINE_SKELETON.animations.horizontal, row: left, flipped: false };

  // Диагональ идёт ровным шагом через всё поле, поэтому средний ряд стоит между крайними.
  if (middle - left === right - middle && Math.abs(middle - left) === 1)
    return { animation: WINLINE_SKELETON.animations.diagonal, row: left, flipped: middle < left };

  // Галка: края на одном ряду, середина на соседнем.
  if (left === right && Math.abs(middle - left) === 1)
    return { animation: WINLINE_SKELETON.animations.zigZag, row: left, flipped: middle > left };

  return null;
}

/** Видимая часть дорожки в координатах картинки барабанов. */
export interface TrackCut {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Считает, какую часть дорожки оставить на виду.
 *
 * Дорожки нарисованы на все три барабана, а комбинация может кончиться раньше: линии, которые
 * делят между собой первые две клетки, дают короткий выигрыш рядом с длинным. Хвост такой
 * дорожки срезается по правому краю последнего выигравшего окна, и путь обрывается там же,
 * где кончились символы.
 *
 * Комбинации во всю линию срезать нечего.
 */
export function trackCut(count: number, reels: number, rows: number): TrackCut | null {
  if (count >= reels)
    return null;

  const left: number = columnX(0, reels) - REELS_GRID.windowWidth / 2;
  const right: number = columnX(count - 1, reels) + REELS_GRID.windowWidth / 2;

  return {
    x: left,
    y: rowY(0, rows) - REELS_GRID.windowHeight / 2,
    width: right - left,
    height: (rows - 1) * REELS_GRID.main.stepY + REELS_GRID.windowHeight,
  };
}

/**
 * Что показу выигрыша нужно от барабанов.
 *
 * Символы на поле держат барабаны, и на время показа выигравшие снимаются с них: их место
 * занимает анимация.
 */
export interface WinField {
  hideSymbols(cells: readonly Cell[]): void;
  showSymbols(): void;
}

/**
 * Отклик на выигрыш: то, что игра делает разом с всплытием суммы.
 *
 * Это гудок паровоза и клуб пара из-под котла, см. ui/smoke.ts. Идут они одним движением
 * и порознь не бывают, см. ADR-49, поэтому показ зовёт отклик одной строкой и не знает,
 * из чего он собран.
 */
export interface WinBurst {
  /** Пускает отклик. */
  start(): void;

  /** Обрывает отклик: показ кончился или игрок начал новый спин. */
  stop(): void;
}

/**
 * Что показывается сейчас.
 *
 * none — показывать нечего.
 * all — общая часть: все линии разом, а за ними сумма выигрыша. Раунд спина ждёт её конца.
 * cycle — линии по одной, по кругу, до следующего спина.
 */
type ShowPhase = 'none' | 'all' | 'cycle';

/** Символ, который ждёт своей очереди: анимации трогаются слева направо вслед за дорожкой. */
interface WaitingSymbol {
  readonly cell: Cell;
  readonly symbol: Spine;
  readonly animation: string;

  /** На какой секунде показа символ трогается. */
  readonly at: number;
}

/** Показ выигрыша поверх игрового поля. */
export class WinShow {
  readonly view: Container = new Container();

  readonly #field: WinField;
  readonly #burst: WinBurst;
  readonly #symbols: SymbolLibrary;
  readonly #assets: AssetManager;
  readonly #paylines: readonly Payline[];
  readonly #reels: number;
  readonly #rows: number;

  /** Символы, которые подменяют собой любой другой. Они играют свою анимацию всегда. */
  readonly #wild: ReadonlySet<SymbolId>;

  // Слои идут снизу вверх: анимации символов стоят в окнах, дорожка линии проходит по ним,
  // а сумма выигрыша ложится поверх всего.
  readonly #symbolLayer: Container = new Container();
  readonly #trackLayer: Container = new Container();

  /**
   * Сумма в середине поля. Всплывает и гаснет целиком, поэтому цифры лежат в своём узле.
   *
   * В общей части это весь выигрыш спина, в цикле — выплата показанной линии. Место у обеих
   * сумм одно: игрок читает их одну за другой и ищет глазами там же, где нашёл первую.
   */
  readonly #amount: AmountLabel;

  /** Вся сумма выигрыша спина в центах. До неё доходит счётчик. */
  #total = 0;

  /** Всплывает ли сумма в середине поля. Ложь — крупный выигрыш называют створки, см. Big Win. */
  #counted = true;

  /** Всё, что заведено на этот показ. Обрыв показа снимает их разом. */
  readonly #played: Spine[] = [];

  /** Маски, которыми срезаны хвосты коротких дорожек. Уходят вместе с ними. */
  readonly #cuts: Graphics[] = [];

  /** Ячейки, чьи анимации уже тронулись: их статичные символы сняты с барабанов. */
  readonly #started: Cell[] = [];

  /** Символы, до которых очередь ещё не дошла. */
  #waiting: WaitingSymbol[] = [];

  /** Кого разбудить, когда кончится общая часть. Раунд спина ждёт только её. */
  #onFinished: (() => void) | null = null;

  /** Итог спина, который показывается. Цикл берёт из него линии и символы. */
  #spin: SpinResult | null = null;

  /** Какая линия идёт в цикле. */
  #line = 0;

  #phase: ShowPhase = 'none';

  /** Время внутри части: в общей — от её начала, в цикле — от начала линии. */
  #seconds = 0;

  /** Когда отыграет последний символ. Сумма всплывает после него. */
  #symbolSeconds = 0;

  constructor(config: GameConfig, symbols: SymbolLibrary, assets: AssetManager, field: WinField, burst: WinBurst) {
    this.#field = field;
    this.#burst = burst;
    this.#symbols = symbols;
    this.#assets = assets;
    this.#paylines = config.paylines;
    this.#reels = config.reels.length;
    this.#rows = config.rows;
    this.#wild = new Set<SymbolId>(config.symbols.filter((symbol: GameSymbol) => symbol.role === 'wild').map((symbol: GameSymbol) => symbol.id));

    // Сумма стоит серединой в середине поля и держится там: по ширине она центрируется сама.
    this.#amount = new AmountLabel(assets, AMOUNT_HEIGHT);
    this.#amount.view.position.set(REELS_GRID.centerX, REELS_GRID.main.centerY);
    this.#amount.view.visible = false;

    this.view.addChild(this.#symbolLayer, this.#trackLayer, this.#amount.view);
  }

  /**
   * Показывает итог спина и ждёт конца общей части.
   *
   * Дальше линии идут по кругу, но этого раунд спина уже не ждёт: барабаны стоят, и держать
   * игру ради цикла незачем. Спин, где не выпало ни выигрыша, ни wild, показывать нечего,
   * и ожидание кончается сразу. Снятый показ кончается тем же ожиданием: раунду ждать больше нечего.
   *
   * @param counted Всплывает ли сумма в середине поля. Ложь — общая часть кончается вместе
   * с анимациями символов, а выигрыш называет Big Win на створках, см. GDD, разделы 3.4 и 3.7.
   */
  play(spin: SpinResult, counted: boolean = true): Promise<void> {
    this.stop();

    const cells: AnimatedCell[] = animatedCells(spin.field, spin.wins, this.#wild, spin.marks);
    if (cells.length === 0)
      return Promise.resolve();

    this.#spin = spin;
    this.#prepareSymbols(spin.field, cells);

    for (const win of spin.wins)
      this.#playTrack(win);

    this.#total = spinPayout(spin);
    this.#counted = counted;
    this.#phase = 'all';
    this.#seconds = 0;

    // Первый барабан трогается разом с дорожкой, а не через кадр.
    this.#startSymbols();

    return new Promise<void>((resolve) => {
      this.#onFinished = resolve;
    });
  }

  /** Снимает показ целиком, возвращает поле к обычному виду и кончает ожидание. */
  stop(): void {
    this.#clearField();

    // Спрайты цифр остаются на месте: они переживают показ и пойдут в дело на следующем.
    this.#amount.view.visible = false;
    this.#amount.clear();
    this.#phase = 'none';
    this.#spin = null;
    this.#burst.stop();
    this.#reportFinished();
  }

  update(seconds: number): void {
    if (this.#phase === 'none')
      return;

    this.#seconds += seconds;
    this.#startSymbols();

    if (this.#phase === 'cycle') {
      if (this.#seconds >= LINE_SECONDS)
        this.#nextLine();

      return;
    }

    // Пока символы играют свою анимацию, сумму показывать рано.
    const passed: number = this.#seconds - this.#symbolSeconds;
    if (passed < 0)
      return;

    // Мигал один wild, а платить не за что: сумма не всплывает, и крутить в цикле нечего.
    if (this.#spin === null || this.#spin.wins.length === 0) {
      this.stop();
      return;
    }

    // Крупный выигрыш называют створки, а не поле: они сходятся сразу за анимациями символов,
    // см. GDD, раздел 3.4. Отклик идёт своим чередом — паровоз гудит и пускает пар.
    if (!this.#counted) {
      this.#burst.start();
      this.#beginCycle();
      return;
    }

    // Счёт идёт уже на всплытии, поэтому в общую длину набор непрозрачности не входит.
    const shown: number = COUNT_SECONDS + HOLD_SECONDS + FADE_SECONDS;
    if (passed >= shown) {
      this.#beginCycle();
      return;
    }

    // Сумма всплывает один раз за показ, поэтому её появление и есть тот миг, к которому
    // привязан отклик: паровоз гудит, и из-под котла бьёт пар.
    if (!this.#amount.view.visible) {
      this.#amount.view.visible = true;
      this.#burst.start();
    }

    this.#amount.draw(countedAmount(this.#total, passed, COUNT_SECONDS));

    // Всплытие и угасание считаются одной формулой: сумма набирает и теряет непрозрачность
    // с одного края, а между ними держится единица.
    this.#amount.view.alpha = Math.min(passed, shown - passed, FADE_SECONDS) / FADE_SECONDS;
  }

  /**
   * Кончает общую часть и пускает линии по кругу.
   *
   * Раунд спина в этот миг возвращает игру в ожидание: дальше показ идёт поверх стоящих
   * барабанов и ничего не держит.
   *
   * Отклик здесь не обрывается: дым идёт свою раскадровку и тает сам. Обрывает его только
   * новый спин, а на Big Win он и вовсе начинается в этот самый миг.
   *
   * Сумму спина с поля не снимают: её место сразу занимает выплата первой линии.
   */
  #beginCycle(): void {
    this.#reportFinished();

    this.#phase = 'cycle';
    this.#line = -1;
    this.#nextLine();
  }

  /** Показывает следующую линию с её выплатой, а за последней снова первую. */
  #nextLine(): void {
    const spin: SpinResult | null = this.#spin;
    if (spin === null)
      return;

    this.#line = (this.#line + 1) % spin.wins.length;
    this.#seconds = 0;

    const win: LineWin = spin.wins[this.#line];

    this.#clearField();
    this.#prepareSymbols(spin.field, winAnimations(winningCells([win]), spin.marks));
    this.#playTrack(win);
    this.#startSymbols();
    this.#showPayout(win);
  }

  /**
   * Пишет в середине поля, сколько принесла показанная линия.
   *
   * Число встаёт разом и целиком: считать его нечего. Счётчик называет весь выигрыш спина,
   * то есть итог, которого игрок ждёт, а линия в цикле отвечает на другой вопрос — за что
   * из этого итога заплачено. Бежала бы и она, игрок читал бы её дольше, чем стоит сама линия.
   */
  #showPayout(win: LineWin): void {
    this.#amount.draw(win.payout);
    this.#amount.view.alpha = 1;
    this.#amount.view.visible = true;
  }

  /** Снимает с поля анимации и дорожки, вернув барабанам их символы. */
  #clearField(): void {
    for (const spine of this.#played)
      spine.destroy();

    for (const shade of this.#cuts)
      shade.destroy();

    this.#cuts.length = 0;
    this.#played.length = 0;
    this.#started.length = 0;
    this.#waiting = [];
    this.#field.showSymbols();
  }

  /** Будит того, кто ждёт конца общей части. Второй раз будить некого. */
  #reportFinished(): void {
    const finished: (() => void) | null = this.#onFinished;

    this.#onFinished = null;
    finished?.();
  }

  /**
   * Заводит анимации символов и ставит каждую в очередь по её барабану.
   *
   * До своей очереди скелет не показывается: в исходной позе у него горят блики, которые
   * анимация гасит первым же ключом.
   */
  #prepareSymbols(field: Field, cells: readonly AnimatedCell[]): void {
    this.#symbolSeconds = 0;

    for (const { cell, animation } of cells) {
      const symbol: Spine = this.#symbols.spine(field[cell.row][cell.reel]);

      // Скелет символа нарисован вокруг своего начала координат, поэтому он встаёт
      // прямо в середину ячейки, как и статичная картинка.
      symbol.position.set(columnX(cell.reel, this.#reels), rowY(cell.row, this.#rows));
      symbol.visible = false;

      const at: number = cell.reel * SYMBOL_STEP_SECONDS;
      const duration: number = symbol.skeleton.data.findAnimation(animation)?.duration ?? 0;

      this.#symbolSeconds = Math.max(this.#symbolSeconds, at + duration);
      this.#waiting.push({ cell, symbol, animation, at });

      this.#played.push(symbol);
      this.#symbolLayer.addChild(symbol);
    }
  }

  /**
   * Пускает анимации, до которых дошла очередь.
   *
   * Анимация играет один раз, как в референсе. Дальше символ стоит в её последней позе:
   * блики к концу гаснут, и ячейка снова выглядит обычным символом.
   *
   * Статичный символ снимается с барабана ровно в этот миг, а не раньше: до своей очереди
   * ячейка обязана выглядеть как всегда.
   */
  #startSymbols(): void {
    const waiting: WaitingSymbol[] = [];

    for (const pending of this.#waiting) {
      if (pending.at > this.#seconds) {
        waiting.push(pending);
        continue;
      }

      pending.symbol.visible = true;
      pending.symbol.state.setAnimation(0, pending.animation, false);
      this.#started.push(pending.cell);
    }

    if (waiting.length === this.#waiting.length)
      return;

    this.#waiting = waiting;
    this.#field.hideSymbols(this.#started);
  }

  /**
   * Пускает дорожку по линии выигрыша.
   *
   * Убирать дорожку не надо, анимация в конце сама снимает путь со скелета. Поэтому к тому
   * времени, когда всплывает сумма, поле остаётся с одними анимациями символов.
   */
  #playTrack(win: LineWin): void {
    const payline: Payline | undefined = this.#paylines[win.payline];
    if (payline === undefined) {
      console.warn(`Выигрыш на линии ${win.payline}, а в описании игры её нет`);
      return;
    }

    const shape: TrackShape | null = trackFor(payline);
    if (shape === null) {
      console.warn(`Для линии ${win.payline} нет дорожки: форма [${payline.join(', ')}] не нарисована`);
      return;
    }

    const track: Spine = this.#assets.spine(WINLINE_SKELETON);

    // Дорожка нарисована от левого края поля вправо, поэтому её начало стоит на краю,
    // а не в середине первого окна. Отражение по вертикали разворачивает её форму.
    track.position.set(columnX(0, this.#reels) - REELS_GRID.windowWidth / 2, rowY(shape.row, this.#rows));
    track.scale.set(1, shape.flipped ? -1 : 1);
    track.state.setAnimation(0, shape.animation, false);

    this.#played.push(track);
    this.#trackLayer.addChild(track);

    const cut: TrackCut | null = trackCut(win.count, this.#reels, this.#rows);
    if (cut === null)
      return;

    // Маска живёт в слое дорожек рядом со своей дорожкой: Pixi рисует её отдельно от неё.
    const shade: Graphics = new Graphics().rect(cut.x, cut.y, cut.width, cut.height).fill(0xffffff);

    track.mask = shade;
    this.#cuts.push(shade);
    this.#trackLayer.addChild(shade);
  }
}
